import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { LanguageTypes, RepresentativeAccessTypes, RepresentativeTypes } from '@shared/enums';
import { Representative } from '@shared/models';

export type RepresentativeFormMode = 'add' | 'edit' | 'view';

export interface RepresentativeFormControls {
  NameFirst: FormControl<string | null>;
  NameLast: FormControl<string | null>;
  EmailAddress: FormControl<string | null>;
  PhoneNumber: FormControl<string | null>;
  LanguageType: FormControl<LanguageTypes | null>;
  AccessType: FormControl<RepresentativeAccessTypes | null>;
  RepresentativeType: FormControl<RepresentativeTypes | null>;
}

export type RepresentativeFormGroup = FormGroup<RepresentativeFormControls>;

@Injectable()
export class RepresentativeFormService {
  private readonly fb = inject(NonNullableFormBuilder);

  private readonly mode$$ = new BehaviorSubject<RepresentativeFormMode>('add');
  readonly mode$ = this.mode$$.asObservable();

  private readonly representativeForm: RepresentativeFormGroup = this.fb.group<RepresentativeFormControls>({
    NameFirst: this.fb.control<string | null>(null, { validators: [Validators.required] }),
    NameLast: this.fb.control<string | null>(null, { validators: [Validators.required] }),
    EmailAddress: this.fb.control<string | null>(null, { validators: [Validators.required, Validators.email] }),
    PhoneNumber: this.fb.control<string | null>(null, { validators: [Validators.required] }),
    LanguageType: this.fb.control<LanguageTypes | null>(LanguageTypes.English, { validators: [Validators.required] }),
    AccessType: this.fb.control<RepresentativeAccessTypes | null>(null),
    RepresentativeType: this.fb.control<RepresentativeTypes | null>(null),
  });

  getForm(): RepresentativeFormGroup {
    return this.representativeForm;
  }

  getMode(): RepresentativeFormMode {
    return this.mode$$.value;
  }

  initializeForAdd(): void {
    this.mode$$.next('add');
    this.resetTo(null);
    this.representativeForm.enable();
  }

  initializeForView(representative: Representative): void {
    this.mode$$.next('view');
    this.resetTo(representative);
    this.representativeForm.disable();
  }

  initializeForEdit(representative: Representative): void {
    this.mode$$.next('edit');
    this.resetTo(representative);
    this.representativeForm.enable();
    // Email is immutable after creation - matches the legacy Representative edit form, which renders it read-only.
    this.representativeForm.controls.EmailAddress.disable();
  }

  private resetTo(representative: Representative | null): void {
    this.representativeForm.reset({
      NameFirst: representative?.NameFirst ?? null,
      NameLast: representative?.NameLast ?? null,
      EmailAddress: representative?.EmailAddress ?? null,
      PhoneNumber: representative?.PhoneNumber ?? null,
      LanguageType: representative?.LanguageType ?? LanguageTypes.English,
      AccessType: representative?.AccessType ?? null,
      RepresentativeType: representative?.RepresentativeType ?? null,
    });
  }
}
